import React, { useEffect, useRef } from "react";
import { useSubscription } from "@apollo/client/react";
import { useRouter } from "next/router";
import { useAuth } from "@/src/AuthContext";
import { toast } from "sonner";
import { CircleCheck, X, Eye } from "lucide-react";
import { updateSingleOrder, initOrders, addOrder } from "@/src/actions";
import { COMPANY_TRIP_UPDATED_SUBSCRIPTION } from "@/graphql/subscriptions/companyTripUpdated";
import { TRIP_CREATED_SUBSCRIPTION } from "@/graphql/subscriptions/tripCreated";
import { getUserId } from "@/utils/index";
import { useOrder } from "@/src/OrderContext";
import { useOrderFilter } from "@/src/OrderFilterContext";
import moment from "moment";

// Statuses each board filter would fetch (mirrors pages/home.js showMainFilters).
const STATUS_BY_FILTER = {
  all: ["started", "arrived", "in progress", "scheduled", "scheduled_accepted", "pending"],
  UPCOMING: ["scheduled", "scheduled_accepted", "pending"],
  ACTIVE: ["started", "arrived", "in progress"],
  PAST: ["drop_off", "completed", "driver_canceled", "client_canceled", "provider_canceled", "assign_failed", "timedout"],
};

// Would a refresh of the CURRENT view show this trip? Used to decide whether a
// real-time update for a trip not yet in the list should be inserted live.
// Narrowed views (search / top-bar filter) are skipped: we lack the date/business/
// provider context here, so we don't risk injecting a row a refresh wouldn't show.
const orderBelongsInView = (trip, fltr, filterState) => {
  if (!trip || !trip.status) return false;
  if (filterState?.searchValue) return false;
  if (filterState?.topBarFilter) return false;
  const allowed = STATUS_BY_FILTER[fltr] || (fltr ? [fltr] : STATUS_BY_FILTER.all);
  return allowed.includes(trip.status);
};

const NotificationHandler = () => {
  const { user } = useAuth();
  const router = useRouter();
  const { dispatch: orderDispatch } = useOrder();
  const { state: orderFilterState, dispatch: orderFilterDispatch } = useOrderFilter();

  const lastNotifiedMessages = useRef({});

  const allowedPages = ["/home", "/chat", "/create-trip"];
  const isAllowedPage = allowedPages.includes(router.pathname);

  const userRole = user?.user?.type?.toLowerCase();
  const isAllowedRole = ["company", "admin", "super_admin"].includes(userRole);

  const companyId = getUserId(user?.user);

  // Subscriptions
  useSubscription(COMPANY_TRIP_UPDATED_SUBSCRIPTION, {
    variables: { companyId },
    skip: !companyId || !isAllowedRole || !isAllowedPage,
    fetchPolicy: 'no-cache',
    onData: ({ data }) => {
      const trip = data.data?.companyTripUpdated;
      if (!trip) return;

      orderDispatch({ type: "SYNC_REALTIME_NOTIFICATIONS", payload: trip });

      if (router.pathname === "/home") {
        const fltr = localStorage.getItem("filter") || "all";
        orderDispatch(
          updateSingleOrder(trip, fltr, false, orderBelongsInView(trip, fltr, orderFilterState))
        );
      }

      if (trip.notification_message) {
        const lastMessage = lastNotifiedMessages.current[trip.id];

        if (lastMessage === trip.notification_message) {
          return;
        }

        lastNotifiedMessages.current[trip.id] = trip.notification_message;
        renderToast(trip, trip.notification_message);
      }
    }
  });

  useSubscription(TRIP_CREATED_SUBSCRIPTION, {
    skip: !companyId || !isAllowedRole || !isAllowedPage,
    fetchPolicy: 'no-cache',
    onData: ({ data }) => {
      const trip = data.data?.tripCreated;
      if (!trip || !trip.id) return;

      orderDispatch(addOrder(trip));
      orderDispatch({ type: "TRIGGER_REFRESH" });
      renderToast(trip, "New Order Created");
    }
  });

  const handleShowTrip = (tripId) => {
    if (router.pathname !== "/home") {
      orderFilterDispatch({ type: "UPDATE_FILTER", payload: "all" });
      orderFilterDispatch({ type: "UPDATE_TOPBAR_FILTER", payload: null });
      orderFilterDispatch({ type: "UPDATE_SEARCH_VALUE", payload: tripId });
      orderDispatch(initOrders([]));
      router.push("/home");
    } else {
      orderFilterDispatch({ type: "UPDATE_FILTER", payload: "all" });
      orderFilterDispatch({ type: "UPDATE_TOPBAR_FILTER", payload: null });
      orderFilterDispatch({ type: "UPDATE_SEARCH_VALUE", payload: tripId });
      orderDispatch(initOrders([]));
    }
  };

  const renderToast = (trip, message) => {
    const tripIdFull = trip.id;

    try {
      const audio = new Audio("/assets/notification/notification.mp3");
      audio.play();
    } catch (error) {
      console.error("Error playing sound:", error);
    }

    const clientObj = trip.client || trip.business_client || trip.booker_client;
    const guestClient = trip.guest_client || trip.invited_member;
    const paxName = guestClient?.full_name || (clientObj ? `${clientObj.first_name || ""} ${clientObj.last_name || ""}`.trim() : "N/A");
    const scheduledAt = moment(trip.schedule_date);

    toast.custom((t) => (
      <div
        onClick={() => {
          handleShowTrip(tripIdFull);
          toast.dismiss(t);
        }}
        className="w-[450px] max-w-[95vw] bg-brand-green cursor-pointer group pointer-events-auto flex items-center gap-4 overflow-hidden rounded-xl p-4 shadow-2xl animate-in fade-in slide-in-from-right-4 duration-300 ring-1 ring-black/5 hover:ring-black/20 transition-all font-sans"
      >
        <div className="bg-black/10 p-2.5 rounded-lg shrink-0 group-hover:bg-black/20 transition-colors">
          <CircleCheck className="h-6 w-6 text-black" />
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <div className="text-black text-[14px] font-bold leading-tight truncate">
            {message}
          </div>

          <div className="flex items-center gap-1.5 text-black/80 text-[14px] font-bold uppercase tracking-wider mt-1 truncate whitespace-nowrap overflow-hidden">
            <span>{scheduledAt.format("HH:mm DD MMM")}</span>
            <span className="w-1 h-1 rounded-full bg-black/10 shrink-0" />
            <span className="truncate italic">{paxName}</span>
          </div>

          <div className="text-black/90 text-[12px] font-medium flex items-center gap-1.5 mt-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-black/90 animate-pulse" />
            <span className="opacity-70">Updated at {moment().format("HH:mm")}</span>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            toast.dismiss(t);
          }}
          className="absolute top-2 right-2 p-1 text-black/20 hover:text-black transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  };

  return null;
};

export default NotificationHandler;
